import { Calendar, Plus, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/ui/loading'
import { useLeaveBalance, useLeaveRequests } from '@/hooks'
import { cn } from '@/utils'

const leaveTypes = [
  { name: '年假', total: 10, used: 3, color: 'bg-green-500' },
  { name: '病假', total: 10, used: 0, color: 'bg-rose-500' },
  { name: '事假', total: 5, used: 1, color: 'bg-blue-500' },
]

const leaveRequests = [
  { id: 1, type: '年假', days: 2, startDate: '2024-01-15', status: 'approved' },
  { id: 2, type: '病假', days: 1, startDate: '2024-01-10', status: 'approved' },
  { id: 3, type: '事假', days: 0.5, startDate: '2024-01-05', status: 'rejected' },
]

const statusMap = {
  pending: { label: '待审批', class: 'bg-yellow-100 text-yellow-800' },
  approved: { label: '已通过', class: 'bg-green-100 text-green-800' },
  rejected: { label: '已拒绝', class: 'bg-rose-100 text-rose-800' },
}

export function LeavePage() {
  const { data: balanceData, isLoading: isBalanceLoading } = useLeaveBalance()
  const { data: requestsData, isLoading: isRequestsLoading } = useLeaveRequests()

  const isLoading = isBalanceLoading || isRequestsLoading

  // Default leave types if data not loaded
  const leaveTypes = [
    { name: '年假', key: 'annual', color: 'bg-green-500' },
    { name: '病假', key: 'sick', color: 'bg-rose-500' },
    { name: '事假', key: 'personal', color: 'bg-blue-500' },
  ].map(type => ({
    ...type,
    ...(balanceData?.[type.key as keyof typeof balanceData] || { total: 10, used: 0, remaining: 10 })
  }))

  const leaveRequests = requestsData?.items || []

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Calendar className="w-6 h-6 text-pink-500" />
              请假管理
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              请假申请、审批流程和假期余额
            </p>
          </div>
        </div>
        <Loading className="py-20" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Calendar className="w-6 h-6 text-pink-500" />
            请假管理
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            请假申请、审批流程和假期余额
          </p>
        </div>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          申请请假
        </Button>
      </div>

      {/* Leave Balance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {leaveTypes.map((type) => (
          <Card key={type.name}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{type.name}</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                    {type.total - type.used} <span className="text-sm text-slate-400">/ {type.total} 天</span>
                  </p>
                </div>
                <div className={cn('w-12 h-12 rounded-full flex items-center justify-center', type.color)}>
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="mt-4">
                <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className={cn('h-full rounded-full', type.color)}
                    style={{ width: `${(type.used / type.total) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  已使用 {type.used} 天
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Leave Requests */}
      <Card>
        <CardHeader>
          <CardTitle>请假记录</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-slate-200 dark:divide-slate-800">
            {leaveRequests.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                暂无请假记录
              </div>
            ) : (
              leaveRequests.map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between py-4"
                >
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {request.leave_type} · {request.days} 天
                    </p>
                    <p className="text-sm text-slate-500">
                      {request.start_date} 至 {request.end_date}
                    </p>
                  </div>
                  <Badge className={statusMap[request.status as keyof typeof statusMap]?.class || 'bg-gray-100 text-gray-800'}>
                    {statusMap[request.status as keyof typeof statusMap]?.label || request.status}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
